import { Box, Col } from '@opendoor/atoms/box';
import { Input } from '@opendoor/atoms/input';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { IconChevronLeft } from '@tabler/icons-react';
import {
  isCompensationValid,
  isEarnestDepositValid,
  isMoneyAskValid,
  isPriceValid,
  isStepComplete,
  type OfferPortalWizardContext,
  type OfferPortalWizardEvent,
  WIZARD_STEP_ORDER,
  WizardStep,
} from 'state_machines/OfferPortalWizardMachine';

import { ListingAlerts } from './ListingAlerts';
import { type ListingDetails } from './listingDetails';
import { ListingInformation } from './ListingInformation';
import { SummaryCard } from './SummaryCard';
import { useFieldErrors } from './useFieldErrors';
import {
  CardHeading,
  FIELD_INPUT,
  FieldError,
  WIZARD_COLUMNS,
  WIZARD_FORM_COL,
  WIZARD_PAGE_GUTTER,
  WIZARD_PAGE_WIDTH,
  WizardHeader,
} from './wizardFields';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const FIELD_CARD = 'w-full flex-col gap-4 rounded-[16px] border border-od-border-primary p-4';

// The percent input works in fractions of 1 (0.04 for 4%); the draft stores
// basis points.
const BPS_PER_UNIT = 10_000;
const CENTS_PER_DOLLAR = 100;
const EARNEST_DEPOSIT_PLACEHOLDER_RATE = 0.01;

const getEarnestDepositPlaceholder = (priceCents: number): string =>
  usd.format((priceCents / CENTS_PER_DOLLAR) * EARNEST_DEPOSIT_PLACEHOLDER_RATE);

/**
 * Step 1 of 6 of the offer wizard — the legacy OfferDetailPage's price and
 * concession terms, per design. The wizard machine owns the answers and the
 * step flow; this component renders the machine's context and sends events.
 */
const OfferTermsStep = ({
  listing,
  context,
  send,
}: {
  listing: ListingDetails;
  context: OfferPortalWizardContext;
  send: (event: OfferPortalWizardEvent) => void;
}) => {
  const { shouldShowError, onBlur } = useFieldErrors(context.showError);
  const maxAllowedCents = listing.maxAllowedOfferPriceCents;
  const overCap =
    maxAllowedCents != null && context.priceCents != null && context.priceCents > maxAllowedCents;
  const priceInvalid =
    (shouldShowError('offer-price') && !isPriceValid(context)) ||
    overCap ||
    context.fieldViolations['priceCents'] != null;

  const handlePriceChange = (value: number | null) => {
    send({ type: 'SET_PRICE_CENTS', value: value != null ? Math.round(value * 100) : null });
  };

  const earnestPlaceholder = getEarnestDepositPlaceholder(
    context.priceCents ?? listing.listingPriceCents ?? 0,
  );
  const earnestOverPrice =
    context.earnestDepositCents != null &&
    context.priceCents != null &&
    context.earnestDepositCents > context.priceCents;
  const earnestInvalid =
    (shouldShowError('earnest-deposit') && !isEarnestDepositValid(context)) ||
    earnestOverPrice ||
    context.fieldViolations['earnestDepositCents'] != null;
  const concessionInvalid =
    (shouldShowError('seller-concession') && context.sellerConcessionCents == null) ||
    !isMoneyAskValid(context.sellerConcessionCents, context.priceCents) ||
    context.fieldViolations['concessionsFromSellerCents'] != null;
  const compensationInvalid =
    ((shouldShowError('agent-compensation') || context.buyerAgentCompensationBps != null) &&
      !isCompensationValid(context)) ||
    context.fieldViolations['brokerCommissionRate'] != null;

  const handleEarnestChange = (value: number | null) => {
    send({
      type: 'SET_EARNEST_DEPOSIT_CENTS',
      value: value != null ? Math.round(value * 100) : null,
    });
  };

  const handleConcessionChange = (value: number | null) => {
    send({
      type: 'SET_SELLER_CONCESSION_CENTS',
      value: value != null ? Math.round(value * 100) : null,
    });
  };

  const handleCompensationChange = (value: number | null) => {
    send({
      type: 'SET_BUYER_AGENT_COMPENSATION_BPS',
      value: value != null ? Math.round(value * BPS_PER_UNIT) : null,
    });
  };

  const handleContinue = () => {
    send({ type: 'CONTINUE' });
  };

  return (
    <Box as="section" className="w-full flex-col items-center bg-od-bg-primary pb-28">
      {/* The gutter sits outside the max-width so the two columns keep their
          full 784px; padding on the capped element eats into the content. */}
      <Box className={WIZARD_PAGE_GUTTER}>
        <Col className={WIZARD_PAGE_WIDTH}>
          <Col className="w-full max-w-[493px] gap-3">
            <WizardHeader
              step={1}
              of={WIZARD_STEP_ORDER.length}
              title="Offer terms"
              back={
                <Link
                  href="/make-offer/start"
                  aria-label="Back to search"
                  analyticsName="cosmos-make-offer-back-offer-terms"
                  className="flex size-9 items-center justify-center rounded-[10px] bg-od-bg-secondary text-od-content-primary no-underline"
                >
                  <IconChevronLeft className="size-5" />
                </Link>
              }
            />
          </Col>
          <Box className={WIZARD_COLUMNS}>
            <ListingInformation listing={listing} />
            <Col className={WIZARD_FORM_COL}>
              <ListingAlerts listing={listing} />
              <Col className={FIELD_CARD}>
                <CardHeading htmlFor="offer-price" title="Offer amount" />
                <Input
                  id="offer-price"
                  type="currency"
                  decimals={0}
                  min={0}
                  value={context.priceCents != null ? context.priceCents / 100 : null}
                  onValueChange={handlePriceChange}
                  onBlur={onBlur('offer-price')}
                  placeholder={
                    listing.listingPriceCents != null
                      ? usd.format(listing.listingPriceCents / 100)
                      : '$'
                  }
                  size="xs"
                  className={FIELD_INPUT}
                  aria-label="Offer amount"
                  aria-invalid={priceInvalid}
                  error={priceInvalid}
                  analyticsName="cosmos-make-offer-terms-price"
                />
                {overCap && maxAllowedCents != null ? (
                  <Text role="alert" className="text-body-sm text-od-content-error">
                    Offers on this home are capped at {usd.format(maxAllowedCents / 100)}.
                  </Text>
                ) : null}
                {context.fieldViolations['priceCents'] != null ? (
                  <FieldError>{context.fieldViolations['priceCents']}</FieldError>
                ) : null}
                {shouldShowError('offer-price') && !overCap && !isPriceValid(context) ? (
                  <Text role="alert" className="text-body-sm text-od-content-error">
                    Offer amount cannot be empty
                  </Text>
                ) : null}
              </Col>

              <Col className={FIELD_CARD}>
                <CardHeading htmlFor="earnest-deposit" title="Earnest money deposit" />
                <Input
                  id="earnest-deposit"
                  type="currency"
                  decimals={0}
                  min={0}
                  value={
                    context.earnestDepositCents != null ? context.earnestDepositCents / 100 : null
                  }
                  onValueChange={handleEarnestChange}
                  onBlur={onBlur('earnest-deposit')}
                  placeholder={earnestPlaceholder}
                  size="xs"
                  className={FIELD_INPUT}
                  aria-label="Earnest money deposit"
                  aria-invalid={earnestInvalid}
                  error={earnestInvalid}
                  analyticsName="cosmos-make-offer-terms-earnest"
                />
                {context.fieldViolations['earnestDepositCents'] != null ? (
                  <FieldError>{context.fieldViolations['earnestDepositCents']}</FieldError>
                ) : null}
                {earnestOverPrice ? (
                  <FieldError>Earnest money deposit can’t exceed the offer amount.</FieldError>
                ) : null}
                {shouldShowError('earnest-deposit') && context.earnestDepositCents == null ? (
                  <FieldError>Earnest money deposit cannot be empty</FieldError>
                ) : null}
              </Col>

              <Col className={FIELD_CARD}>
                <CardHeading
                  htmlFor="seller-concession"
                  title="Seller concession"
                  caption="Credits you’re requesting the seller to cover"
                  captionId="seller-concession-description"
                />
                <Input
                  id="seller-concession"
                  type="currency"
                  decimals={0}
                  min={0}
                  value={
                    context.sellerConcessionCents != null
                      ? context.sellerConcessionCents / 100
                      : null
                  }
                  onValueChange={handleConcessionChange}
                  onBlur={onBlur('seller-concession')}
                  placeholder="$0"
                  size="xs"
                  className={FIELD_INPUT}
                  aria-label="Seller concession"
                  aria-required="true"
                  aria-describedby="seller-concession-description"
                  aria-invalid={concessionInvalid}
                  error={concessionInvalid}
                  analyticsName="cosmos-make-offer-terms-concession"
                />
                {context.fieldViolations['concessionsFromSellerCents'] != null ? (
                  <FieldError>{context.fieldViolations['concessionsFromSellerCents']}</FieldError>
                ) : null}
                {shouldShowError('seller-concession') && context.sellerConcessionCents == null ? (
                  <FieldError>Seller concession cannot be empty</FieldError>
                ) : null}
                {!isMoneyAskValid(context.sellerConcessionCents, context.priceCents) ? (
                  <FieldError>Seller concession can’t exceed the offer amount.</FieldError>
                ) : null}
              </Col>

              <Col className={FIELD_CARD}>
                <CardHeading htmlFor="agent-compensation" title="Buyer agent commission" />
                <Input
                  id="agent-compensation"
                  type="percent"
                  // decimals is fraction precision: 4 places = 1bp steps, so
                  // nothing is silently re-rounded against the stored draft.
                  decimals={4}
                  min={0}
                  value={
                    context.buyerAgentCompensationBps != null
                      ? context.buyerAgentCompensationBps / BPS_PER_UNIT
                      : null
                  }
                  onValueChange={handleCompensationChange}
                  onBlur={onBlur('agent-compensation')}
                  placeholder="2.5%"
                  size="xs"
                  className={FIELD_INPUT}
                  aria-label="Buyer agent commission"
                  aria-required="true"
                  aria-invalid={compensationInvalid}
                  error={compensationInvalid}
                  analyticsName="cosmos-make-offer-terms-compensation"
                />
                {context.fieldViolations['brokerCommissionRate'] != null ? (
                  <FieldError>{context.fieldViolations['brokerCommissionRate']}</FieldError>
                ) : null}
                {shouldShowError('agent-compensation') &&
                context.buyerAgentCompensationBps == null ? (
                  <FieldError>Buyer agent commission cannot be empty</FieldError>
                ) : null}
                {context.buyerAgentCompensationBps != null && !isCompensationValid(context) ? (
                  <FieldError>Buyer agent commission can’t exceed 4%.</FieldError>
                ) : null}
              </Col>
            </Col>

            <SummaryCard
              listing={listing}
              offerCents={context.priceCents}
              continueLabel="Continue"
              continueDisabled={!isStepComplete(WizardStep.OfferTerms, context)}
              onContinue={handleContinue}
              analyticsName="cosmos-make-offer-continue-offer-terms"
            />
          </Box>
        </Col>
      </Box>
    </Box>
  );
};

export default OfferTermsStep;
